import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { usePostHog } from "posthog-js/react";
import { useAppDispatch } from "./packages/store/hooks";
import { getCurrentTeacher } from "./packages/store/userSlice";
import i18n from "./packages/i18n/config";
import * as Sentry from "@sentry/react";

import Sidebar from "./packages/layout/Sidebar";
import Navbar from "./packages/layout/Navbar";
import { ToastProvider } from "./packages/contexts/ToastContext";
import { ToastContainer } from "./packages/provider/ToastContainer";
import ProtectedRoute from "./packages/provider/ProtectedRoute";

import { allRoutes } from "./packages/routes";
import { authRoutes } from "./packages/routes/AuthRoutes";
import { trackPageView } from "./packages/hooks/useAnalytics";
import ReactGA from "react-ga4";
import H5PSingle from "@pages/tools/h5p-single-view";
import QuestionSolver from "@pages/tools/question-solver";
import EdlinkReminder from "@packages/layout/EdlinkReminder";
import ProClosedModal from "@features/dasboard/components/ProClosedModal";
import TrialWarningModal from "@features/dasboard/components/TrialWarningModal";

const queryClient = new QueryClient();

function App() {
  const dispatch = useAppDispatch();
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const posthog = usePostHog();

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    dispatch(getCurrentTeacher()).then((res: any) => {
      if (res.payload && res.payload[0]) {
        posthog.identify(res.payload[0].id, {
          user_id: res.payload[0].id,
          email: res.payload[0].email,
          name: res.payload[0].name,
        });
        Sentry.setUser({
          id: res.payload[0].id.toString(),
          email: res.payload[0].email,
          username: `${res.payload[0].name} ${res.payload[0].surname}`.trim(),
        });
        ReactGA.set({
          internal_user_id: res.payload[0].id.toString(),
          user_email: res.payload[0].email,
          user_name: `${res.payload[0].name} ${res.payload[0].surname}`.trim(),
          user_grade: "-1",
          user_premium: "false",
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    });
  }, []);

  return (
    <ToastProvider>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true,
            }}
          >
            <ScrollToTop />
            <Routes>
              {authRoutes}
              <Route
                key="question-solver"
                path="/question"
                element={<QuestionSolver />}
              />
              ,
              <Route key="h5p" path="/h5p/:id" element={<H5PSingle />} />,
              <Route key="h5p-tr" path="/h5p/:id/tr" element={<H5PSingle />} />,
              <Route key="h5p-en" path="/h5p/:id/en" element={<H5PSingle />} />,
              <Route key="h5p-de" path="/h5p/:id/de" element={<H5PSingle />} />,
              <Route key="h5p-de" path="/h5p/:id/fr" element={<H5PSingle />} />,
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
                      <Sidebar
                        isCollapsed={isMobile && isSidebarCollapsed}
                        setIsCollapsed={setIsSidebarCollapsed}
                      />
                      <div
                        className={`flex-1 flex flex-col ${isMobile ? "pt-20" : ""}`}
                      >
                        {isMobile && (
                          <Navbar
                            isSidebarCollapsed={isSidebarCollapsed}
                            setIsSidebarCollapsed={setIsSidebarCollapsed}
                          />
                        )}
                        <Routes>
                          {allRoutes.filter(
                            (route) => route.props.path !== "/login",
                          )}
                        </Routes>
                        <EdlinkReminder />
                      </div>
                    </div>
                  </ProtectedRoute>
                }
              />
            </Routes>
            <ProClosedModal />
            <TrialWarningModal />
          </BrowserRouter>
        </I18nextProvider>
      </QueryClientProvider>
      <ToastContainer />
    </ToastProvider>
  );
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView();
  }, [location.pathname]);

  return null;
}

export default App;
